import { isEmpty, orderBy } from "@/lib/collections";
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Upload, FileDown } from 'lucide-react';
import AprilPicto from '@/components/shared/april-picto';
import { PictoContrats } from '@/assets/icons-april-fr/pictos';
import APILink from '@/components/shared/api-link';
import { formatDate } from '@/lib/dates';
import type { AccidentDocument } from '@/types/api/accident';
import { PDF } from '@/types/api/endpoints';

interface DetailSinistreDocumentsProps {
    contractId: string | number;
    accidentId: string | number;
    documents?: AccidentDocument[];
    situationCode?: string;
    upload_app_link?: string | null;
}

const DetailSinistreDocuments = (props: DetailSinistreDocumentsProps) => {
    const { contractId, accidentId, documents, situationCode, upload_app_link } = props;
    const getDecCirconstancieeLink = PDF.declarationCirconstanciee(contractId);
    const getAttestationNonAlcoolemieLink = PDF.attestationNonAlcoolemie(contractId, accidentId);

    const getBlankDocumentLink = (code: string) => {
        if (code === 'F' && getDecCirconstancieeLink) return getDecCirconstancieeLink;
        if (code === '8' && getAttestationNonAlcoolemieLink) return getAttestationNonAlcoolemieLink;
        return null;
    };

    const getDocBadge = (d: AccidentDocument) => {
        if (!d.recu) {
            return <Badge variant="destructive" className="text-[10px] sm:text-xs">Non reçu</Badge>;
        }
        if (d.traite) {
            return (
                <Badge variant="success" className="text-[10px] sm:text-xs">
                    <span className="sm:hidden">Validé</span>
                    <span className="hidden sm:inline">Validé {d.dateTraitement && `le ${formatDate(d.dateTraitement)}`}</span>
                </Badge>
            );
        }
        return <Badge variant="warning" className="text-[10px] sm:text-xs">
            <span className="sm:hidden">En cours</span>
            <span className="hidden sm:inline">En cours de traitement</span>
        </Badge>;
    };

    return (
        <Card>
            <CardHeader className="pb-6 border-b">
                <CardTitle className="text-lg flex items-center gap-2">
                    <AprilPicto icon={PictoContrats} size="md" />
                    Mes documents
                </CardTitle>
            </CardHeader>
            <CardContent className="pt-6">
                {orderBy(documents, ['recu', 'traite']).map((d) => {
                    const blankLink = !d.recu ? getBlankDocumentLink(d.code) : null;

                    return (
                        <div key={d.code} className="relative p-2 bg-muted/50 rounded-lg mb-4 transition-all duration-200 hover:bg-primary/10 hover:translate-x-1">
                            <div className="absolute top-2 right-2">
                                {getDocBadge(d)}
                            </div>
                            <div className="flex items-center gap-2 sm:gap-3 pr-20 sm:pr-28">
                                <div className="flex items-center justify-center w-8 h-8 sm:w-10 sm:h-10 rounded-lg bg-primary/10 shrink-0">
                                    <AprilPicto icon={PictoContrats} size="sm" />
                                </div>
                                <div className="min-w-0">
                                    <p className="font-medium text-sm sm:text-base truncate">{d.libelle}</p>
                                    {d.commentaire && (
                                        <p className="text-xs sm:text-sm text-muted-foreground truncate">{d.commentaire}</p>
                                    )}
                                    {blankLink && (
                                        <APILink
                                            href={blankLink}
                                            target="_blank"
                                            download
                                            className="inline-flex items-center gap-1 text-xs sm:text-sm text-primary hover:underline mt-1"
                                        >
                                            <FileDown className="w-3.5 h-3.5" />
                                            Télécharger le document vierge
                                        </APILink>
                                    )}
                                </div>
                            </div>
                        </div>
                    );
                })}

                {isEmpty(documents) && (
                    <p className="text-sm text-muted-foreground">Pas de documents.</p>
                )}

                {situationCode !== "2" && situationCode !== "8" && documents?.find((d) => d.recu === false) && upload_app_link && (
                    <div className="text-center mt-6">
                        <Button asChild variant="outline" className="cursor-pointer">
                            <a href={upload_app_link} target="_blank" rel="noopener noreferrer">
                                <Upload className="w-4 h-4" />
                                J'envoie mes documents
                            </a>
                        </Button>
                    </div>
                )}
            </CardContent>
        </Card>
    );
};

export default DetailSinistreDocuments;
